import { cn, focusRing } from "@simpro/ui";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import { useSalonClock, useSalonMoney } from "@/features/salon";
import { pill } from "@/shared/styles/pill";
import { myAppointmentsQuery } from "../api/myAppointments.queries";
import { bookAgainPath, serviceIdsOf, servicesLabel, servicesTitle, stylistsOf } from "../lib/visitStatus";

/**
 * "Your last visit" on the salon's home page (round 2 C18): for a signed-in customer with a completed
 * visit, a card right under the hero that books the same services again in one tap. Hidden otherwise,
 * never empty: no completed visit, still loading, or the read failed.
 *
 * Restyled under design note salon-booking-premium-redesign.md section 5.3 item 16, in the same
 * language as My bookings: a hairline card with an accent eyebrow, the line in the display face and an
 * ink pill, in place of the coral fill.
 */
export function LastVisitCard() {
  const clock = useSalonClock();
  const formatMoney = useSalonMoney();
  const past = useQuery(myAppointmentsQuery({ scope: "Past", page: 1 }));
  const visit = past.data?.items.find((item) => item.status === "Completed");
  if (!visit) return null;

  const parts = clock.parts(visit.startsAt);
  const stylists = stylistsOf(visit);

  return (
    <section
      aria-label="Your last visit"
      className={cn(
        "flex items-center justify-between gap-4 rounded-card border border-edge-subtle bg-surface px-5 py-4.5 md:gap-6 md:px-7 md:py-6",
        "transition-[transform,border-color] duration-300 hover:-translate-y-0.5 hover:border-edge",
      )}
    >
      <div className="flex min-w-0 flex-col gap-1">
        <p className="eyebrow text-accent-ink">
          Your last visit · {parts.weekday} {parts.day} {parts.month}
        </p>
        <p className="headline text-xl leading-snug text-heading md:text-2xl">Book your usual again</p>
        <p className="truncate text-[13px] text-subtle md:text-[15px]">
          {[servicesTitle(visit), stylists.length > 0 ? stylists.join(", ") : "Anyone", formatMoney(visit.total)].join(" · ")}
        </p>
      </div>
      <Link
        to={bookAgainPath(serviceIdsOf(visit))}
        aria-label={`Book ${servicesLabel(visit)} again`}
        className={cn(pill.base, pill.ink, "h-11 px-5 text-sm md:h-12 md:px-6 md:text-[15px]", focusRing)}
      >
        Book
      </Link>
    </section>
  );
}
